import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { generateShader, loadShaderProviderDefaults, loadShaderProviderSecrets } from '@/lib/canvas/shader/authoring'
import { useShaderCodeModelHost } from '@/lib/canvas/shader/authoring/host-context'
import { validateShader } from '@/lib/canvas/shader/validate'
import { resolveShaderProviderConfig } from '@/components/canvas/shader/node-provider'
import { useShaderGenerationScope } from '@/components/canvas/shader/ShaderGenerationScope'
import {
  activeRun,
  cancelRun,
  finishRun,
  publishRun,
  runKey,
  runSnapshot,
  startRun,
  subscribeRun,
  type ActiveRun,
  type StateChange,
} from '@/components/canvas/shader/generation-runs'
import {
  shaderStateInputTypes,
  toGenerationFailure,
  withGeneratedShader,
  withGenerationError,
  type GenerationFailure,
} from '@/components/canvas/shader/generation-state'
import type { ShaderGenerationProgress, ShaderGenerationRequest } from '@/lib/canvas/shader/authoring/types'
import type { ShaderInputType } from '@/lib/canvas/shader/media/contract'
import type { RuntimeMode } from '@/lib/canvas/shader/types'

type State = Record<string, unknown>

/** Seams for tests; production uses the real engine, storage and validator. */
export interface ShaderGenerationDeps {
  generate: typeof generateShader
  loadDefaults: typeof loadShaderProviderDefaults
  loadSecrets: typeof loadShaderProviderSecrets
  validate: typeof validateShader
}

const DEFAULT_DEPS: ShaderGenerationDeps = {
  generate: generateShader,
  loadDefaults: loadShaderProviderDefaults,
  loadSecrets: loadShaderProviderSecrets,
  validate: validateShader,
}

export interface ShaderGeneration {
  run: (prompt: string) => void
  cancel: () => void
  running: boolean
  progress: ShaderGenerationProgress | null
  error: GenerationFailure | null
  /** Successful generations so far; the composer clears its draft when this grows. */
  completed: number
}

/**
 * Writes or edits a Shader node's code from a prompt. Progress lives here, not
 * in node state, so a whole generation lands as one state change — one undo step.
 */
export function useShaderGeneration(
  nodeId: string,
  state: State,
  onStateChange: StateChange | undefined,
  connectedInputs: readonly ShaderInputType[],
  deps: Partial<ShaderGenerationDeps> = {},
): ShaderGeneration {
  const host = useShaderCodeModelHost()
  const scope = useShaderGenerationScope()
  const key = runKey(scope, nodeId)
  const snapshot = useSyncExternalStore(
    useCallback((listener: () => void) => subscribeRun(key, listener), [key]),
    () => runSnapshot(key),
  )
  const latest = useRef({ state, onStateChange, connectedInputs, host, deps })

  useEffect(() => {
    latest.current = { state, onStateChange, connectedInputs, host, deps }
    // A run started before this instance mounted (in this editor) delivers through the newest updater.
    const active = activeRun(key)
    if (active && onStateChange) active.apply = onStateChange
  })

  const run = useCallback((rawPrompt: string) => {
    const prompt = rawPrompt.trim()
    if (!prompt || activeRun(key)) return
    const { state: current, connectedInputs: inputs, host: codeHost, deps: overrides } = latest.current
    const { generate, loadDefaults, loadSecrets, validate } = { ...DEFAULT_DEPS, ...overrides }

    const code = typeof current.code === 'string' ? current.code : ''
    const runtimeMode = (current.runtimeMode as RuntimeMode | undefined) ?? 'glsl'
    const mode = code.trim() ? 'edit' : 'create'
    // Node state may come from someone else's canvas: it never chooses where the prompt goes.
    const config = resolveShaderProviderConfig(current.provider, loadDefaults())
    const request: ShaderGenerationRequest = {
      prompt,
      mode,
      connectedInputs: [...inputs],
      ...(mode === 'edit'
        ? { current: { code, runtimeMode, inputTypes: shaderStateInputTypes(current) } }
        : { preferredRuntime: runtimeMode }),
    }

    const controller = new AbortController()
    const active: ActiveRun = { scope, nodeId, controller, apply: latest.current.onStateChange }
    startRun(key, active)
    publishRun(key, { running: true, error: null, progress: { phase: 'generating', received: 0 } })

    const isCurrent = () => activeRun(key) === active && !controller.signal.aborted

    generate({
      request,
      config,
      secrets: loadSecrets(),
      host: codeHost,
      validate,
      signal: controller.signal,
      onProgress: (next) => { if (isCurrent()) publishRun(key, { progress: next }) },
    })
      .then((result) => {
        if (!isCurrent()) return
        active.apply?.((prev) =>
          withGeneratedShader(prev, { result, prompt, mode, config, entryId: crypto.randomUUID() }),
        )
        publishRun(key, { completed: runSnapshot(key).completed + 1 })
      })
      .catch((cause: unknown) => {
        const failure = toGenerationFailure(cause)
        // Cancelling is the user's choice, not a failure worth recording on the node.
        if (!isCurrent() || failure.code === 'ABORTED') return
        publishRun(key, { error: failure })
        active.apply?.((prev) => withGenerationError(prev, failure, prompt, {
          config,
          mode,
          entryId: crypto.randomUUID(),
        }))
      })
      .finally(() => {
        if (finishRun(key, active)) publishRun(key, { running: false, progress: null })
      })
  }, [key, scope, nodeId])

  const cancel = useCallback(() => cancelRun(key), [key])

  return { run, cancel, ...snapshot }
}
